'use client'

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useConversationOptimized as useConversation } from '@/hooks/useConversationOptimized'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import ConversationsSidebar from '@/components/conversation/ConversationsSidebar'
import StandardChatContainer from './StandardChatContainer'
import SEO from '@/components/layout/SEO'

export default function ConversationPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isLoading: authLoading } = useAuth()
  const [showSidebar, setShowSidebar] = useState(false)
  const [loadGuardExpired, setLoadGuardExpired] = useState(false)

  const {
    conversation,
    messages,
    isLoading,
    educationProgress
  } = useConversation(id)

  // Sidebar responsive init
  useEffect(() => {
    setShowSidebar(window.innerWidth >= 1024)
  }, [])

  // Load guard timer
  useEffect(() => {
    let tid: number | undefined
    const loading = authLoading || (isLoading && messages.length === 0 && !conversation)
    if (loading) tid = window.setTimeout(() => setLoadGuardExpired(true), 3000)
    else { setLoadGuardExpired(false); if (tid) window.clearTimeout(tid) }
    return () => { if (tid) window.clearTimeout(tid) }
  }, [authLoading, isLoading, messages.length, conversation])

  // Redirect to new dedicated education session page
  useEffect(() => {
    if (conversation?.category?.is_educational && (conversation.metadata as any)?.session_type === 'education') {
      const catId = (conversation.category as any).id
      const metadata = conversation.metadata as any
      const classParam = metadata.class_id ? `&class=${metadata.class_id}` : ''
      navigate(`/learn/session?category=${catId}${classParam}`, { replace: true })
    }
  }, [conversation, navigate])

  const initialLoading = authLoading || (isLoading && messages.length === 0 && !conversation)

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f2' }}>
        <div className="text-center space-y-4">
          <LoadingSpinner />
          {loadGuardExpired && (
            <div className="space-y-3">
              <p style={{ color: '#4a5568' }}>This is taking longer than usual.</p>
              <div className="flex items-center justify-center gap-3">
                <Button onClick={() => navigate('/conversations')} style={{ background: '#4a7c5e', color: 'white' }}>Back to Conversations</Button>
                <Button onClick={() => window.location.reload()} variant="ghost">Reload</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f2' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a2740' }}>Conversation not found</h2>
          <p className="mb-6" style={{ color: '#4a5568' }}>This conversation may have been deleted.</p>
          <Button onClick={() => navigate('/dashboard')} style={{ background: '#4a7c5e', color: 'white' }}>Back to Dashboard</Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex h-full overflow-hidden"
      style={{ background: '#faf7f2', fontFamily: "'Source Serif 4', Georgia, serif" }}
    >
      <SEO title={conversation?.title || "Conversation"} noindex />
      <ConversationsSidebar
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        currentConversationId={id}
        onConversationSelect={(cid) => navigate(`/conversation/${cid}`)}
      />

      <div className="flex flex-col flex-1 min-w-0 h-full overflow-hidden">
        {(conversation.metadata as any)?.session_type !== 'education' && (
          <StandardChatContainer
            id={id as string}
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
          />
        )}
      </div>
    </div>
  )
}